import { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook, faInstagram, faXTwitter, faLinkedin, faTiktok,
} from "@fortawesome/free-brands-svg-icons";

const PUBLISH_URL = process.env.REACT_APP_PUBLISH_URL || "";

const C = {
  navy:    "#0B1F3A",
  red:     "#FF0A4B",
  white:   "#FFFFFF",
  bg:      "#F5F7FA",
  muted:   "#8899aa",
  border:  "#dde3ec",
  charcoal:"#2d3748",
  green:   "#22c55e",
};

const PLATFORMS = [
  { key: "facebook",         label: "Facebook",        color: "#1877F2", icon: faFacebook,  faIcon: true  },
  { key: "instagram",        label: "Instagram",        color: "#E1306C", icon: faInstagram, faIcon: true  },
  { key: "x",                label: "X",                color: "#000000", icon: faXTwitter,  faIcon: true  },
  { key: "tiktok",           label: "TikTok",           color: "#010101", icon: faTiktok,    faIcon: true  },
  { key: "linkedin",         label: "LinkedIn",         color: "#0A66C2", icon: faLinkedin,  faIcon: true  },
  { key: "youtube",          label: "YouTube",          color: "#FF0000", initials: "YT",    faIcon: false },
  { key: "google_analytics", label: "Google Analytics", color: "#E37400", initials: "GA",    faIcon: false },
];

const IMPLICIT = {};

const fmtDate = (ts) => {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function ConnectedAccountsPanel({ activeCampaignId }) {
  const [accounts,      setAccounts]      = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [connecting,    setConnecting]    = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [confirmDisc,   setConfirmDisc]   = useState(null);
  const [syncing,       setSyncing]       = useState(null);
  const popupRef    = useRef(null);
  const msgHandlerRef = useRef(null);

  const fetchAccounts = useCallback(async () => {
    if (!activeCampaignId || !PUBLISH_URL) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${PUBLISH_URL}/oauth/accounts?campaign_id=${activeCampaignId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setAccounts(data.accounts || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeCampaignId]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  useEffect(() => {
    return () => {
      if (msgHandlerRef.current) window.removeEventListener("message", msgHandlerRef.current);
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    };
  }, []);

  const handleConnect = async (platform) => {
    if (!activeCampaignId || !PUBLISH_URL || connecting) return;
    setConnecting(platform);
    setError(null);
    try {
      const res  = await fetch(`${PUBLISH_URL}/oauth/connect?platform=${platform}&campaign_id=${activeCampaignId}`);
      const data = await res.json();
      if (!res.ok || !data.auth_url) throw new Error(data.error || "Failed to get auth URL");

      if (msgHandlerRef.current) window.removeEventListener("message", msgHandlerRef.current);

      const popup = window.open(data.auth_url, "oauth_popup", "width=620,height=720,scrollbars=yes,resizable=yes");
      popupRef.current = popup;

      const handler = (event) => {
        if (!event.data || typeof event.data !== "object") return;
        if (event.data.type === "oauth-complete" || event.data.type === "oauth-error") {
          window.removeEventListener("message", handler);
          msgHandlerRef.current = null;
          if (popup && !popup.closed) popup.close();
          setConnecting(null);
          if (event.data.type === "oauth-complete") fetchAccounts();
          else setError(event.data.error || "Connection failed");
        }
      };
      msgHandlerRef.current = handler;
      window.addEventListener("message", handler);

      const pollClose = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(pollClose);
          if (msgHandlerRef.current === handler) {
            window.removeEventListener("message", handler);
            msgHandlerRef.current = null;
          }
          setConnecting(null);
          fetchAccounts();
        }
      }, 800);
    } catch (e) {
      setError(e.message);
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platform) => {
    if (!activeCampaignId || !PUBLISH_URL || disconnecting) return;
    setDisconnecting(platform);
    setConfirmDisc(null);
    setError(null);
    try {
      const res  = await fetch(
        `${PUBLISH_URL}/oauth/accounts?campaign_id=${activeCampaignId}&platform=${platform}`,
        { method: "DELETE" }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setAccounts(prev => prev.filter(a => a.platform !== platform));
    } catch (e) {
      setError(e.message);
    } finally {
      setDisconnecting(null);
    }
  };

  const handleSyncInstagram = async (igUserId = null) => {
    if (!activeCampaignId || !PUBLISH_URL || syncing) return;
    setSyncing("instagram");
    setError(null);
    try {
      const res  = await fetch(`${PUBLISH_URL}/oauth/sync-instagram`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campaign_id: activeCampaignId, ...(igUserId ? { ig_user_id: igUserId } : {}) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await fetchAccounts();
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncing(null);
    }
  };

  const getAccount = (key) => accounts.find(a => a.platform === key) || null;

  if (!PUBLISH_URL) {
    return (
      <Typography sx={{ fontSize: 11, color: C.muted }}>
        REACT_APP_PUBLISH_URL not configured.
      </Typography>
    );
  }

  return (
    <Box>
      {error && (
        <Box sx={{ mb: 1.5, px: 1.5, py: 1, bgcolor: "#fef2f2", border: `1px solid #fca5a5`, borderRadius: "6px" }}>
          <Typography sx={{ fontSize: 11, color: "#b91c1c" }}>{error}</Typography>
        </Box>
      )}

      {/* Platform rows — match drawer InfoRow aesthetic */}
      <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: 1.5, overflow: "hidden", mb: 1 }}>
        {PLATFORMS.map((p, idx) => {
          const acct         = getAccount(p.key);
          const isConnected  = !!acct;
          const isConnecting = connecting === p.key;
          const isDisc       = disconnecting === p.key;
          const isImplicit   = !!IMPLICIT[p.key];
          const parentKey    = IMPLICIT[p.key];
          const parentAcct   = parentKey ? getAccount(parentKey) : null;

          return (
            <Box
              key={p.key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1,
                borderBottom: idx < PLATFORMS.length - 1 ? `1px solid ${C.border}` : "none",
                bgcolor: C.white,
              }}
            >
              {/* Platform icon */}
              <Box
                sx={{
                  width: 28, height: 28, borderRadius: "7px", flexShrink: 0,
                  bgcolor: isConnected ? p.color : C.bg,
                  border: `1px solid ${isConnected ? "transparent" : C.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {p.faIcon ? (
                  <FontAwesomeIcon icon={p.icon} style={{ fontSize: 14, color: isConnected ? "#fff" : p.color }} />
                ) : (
                  <Typography sx={{ fontSize: 9, fontWeight: 800, color: isConnected ? "#fff" : p.color, letterSpacing: "0.03em" }}>
                    {p.initials}
                  </Typography>
                )}
              </Box>

              {/* Label + status */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.charcoal, lineHeight: 1.2 }}>
                  {p.label}
                </Typography>
                {isConnected ? (
                  <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {acct.platform_username ? `@${acct.platform_username}` : acct.platform_name && !/^\d{10,}$/.test(acct.platform_name) ? acct.platform_name : "Connected"}
                    {acct.connected_at && <Box component="span" sx={{ ml: 0.75 }}>· {fmtDate(acct.connected_at)}</Box>}
                  </Typography>
                ) : isImplicit && !parentAcct ? (
                  <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.15 }}>
                    Connects via {PLATFORMS.find(x => x.key === parentKey)?.label}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 11, color: C.muted, mt: 0.15 }}>
                    Not connected{!isImplicit && p.note ? ` · ${p.note}` : ""}
                  </Typography>
                )}
              </Box>

              {/* Status badge + action */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                {isConnected && (
                  <Box sx={{ px: 0.75, py: 0.2, bgcolor: C.green + "15", border: `1px solid ${C.green}40`, borderRadius: "4px" }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: C.green, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Live
                    </Typography>
                  </Box>
                )}

                {!isImplicit && (
                  confirmDisc === p.key ? (
                    <Box sx={{ display: "flex", gap: 0.75 }}>
                      <Box component="button" onClick={() => setConfirmDisc(null)}
                        sx={{ fontSize: 12, fontWeight: 600, px: 1.5, py: 0.55, bgcolor: C.bg, color: C.muted, border: `1px solid ${C.border}`, borderRadius: "6px", cursor: "pointer" }}>
                        Cancel
                      </Box>
                      <Box component="button" onClick={() => handleDisconnect(p.key)}
                        sx={{ fontSize: 12, fontWeight: 700, px: 1.5, py: 0.55, bgcolor: "#fef2f2", color: "#dc2626", border: `1px solid #fca5a5`, borderRadius: "6px", cursor: "pointer" }}>
                        {isDisc ? "…" : "Confirm"}
                      </Box>
                    </Box>
                  ) : isConnected ? (
                    <Box component="button" onClick={() => setConfirmDisc(p.key)}
                      sx={{ fontSize: 12, fontWeight: 600, px: 1.5, py: 0.55, bgcolor: C.white, color: C.muted, border: `1px solid ${C.border}`, borderRadius: "6px", cursor: "pointer", "&:hover": { color: "#dc2626", borderColor: "#fca5a5" } }}>
                      Disconnect
                    </Box>
                  ) : (
                    <Box component="button"
                      onClick={() => handleConnect(p.key)}
                      disabled={!!connecting}
                      sx={{ fontSize: 12, fontWeight: 700, px: 1.75, py: 0.55, bgcolor: isConnecting ? C.bg : p.color, color: isConnecting ? C.muted : "#fff", border: "none", borderRadius: "6px", cursor: connecting ? "not-allowed" : "pointer", opacity: connecting && !isConnecting ? 0.45 : 1 }}>
                      {isConnecting ? "Opening…" : "Connect"}
                    </Box>
                  )
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 10, color: C.muted, lineHeight: 1.5 }}>
          Tokens encrypted at rest · Disconnect anytime
        </Typography>
        <Box component="button" onClick={fetchAccounts} disabled={loading}
          sx={{ fontSize: 10, fontWeight: 600, px: 1, py: 0.3, bgcolor: C.white, color: loading ? C.muted : C.navy, border: `1px solid ${loading ? C.border : C.navy + "40"}`, borderRadius: "4px", cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? "↻" : "↻ Refresh"}
        </Box>
      </Box>
    </Box>
  );
}
